import React, { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { C, input } from "./theme";

/* Birthplace search. Debounced type-ahead against /v1/geocode (or a custom `search`
   function, e.g. the token-gated intake variant); picking a result resolves
   coordinates. Falls back to manual lat/long entry when the service is down or a place
   can't be found. Calls onResolve({ place, lat, lon }) — lat/lon are numbers-as-strings
   ready for the chart endpoint; place is the human label. */

/* Lenient coordinate parsing: accepts "38.5816", "-121.49", "38.58° N", "121.49 W",
   comma decimals. Returns a number or null. */
export const parseCoord = (raw, isLat) => {
  if (raw == null) return null;
  let s = String(raw).trim().toUpperCase();
  if (s === "") return null;
  const hemi = /[SW]/.test(s) ? -1 : 1;
  s = s.replace(/[NSEW°\s]/g, "").replace(",", ".");
  const n = Number(s);
  if (s === "" || Number.isNaN(n)) return null;
  const v = hemi < 0 ? -Math.abs(n) : n;
  return Math.abs(v) <= (isLat ? 90 : 180) ? v : null;
};

export default function PlaceField({ onResolve, initialPlace = "", search = null }) {
  const [q, setQ] = useState(initialPlace);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");            // "no matches" / error text
  const [picked, setPicked] = useState(null);      // { label, lat, lon }
  const [manual, setManual] = useState(false);
  const [mlat, setMlat] = useState("");
  const [mlon, setMlon] = useState("");
  const timer = useRef(null);

  // Debounced search on query change (skip while in manual mode or right after a pick).
  useEffect(() => {
    if (manual) return;
    if (picked && q === picked.label) return;        // don't re-search the selection
    if (q.trim().length < 3) { setResults([]); setOpen(false); setNote(""); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true); setNote("");
      try {
        const { results: r } = await (search || api.geocode)(q.trim());
        setResults(r); setOpen(true);
        setNote(r.length ? "" : "No matches — check spelling, or enter coordinates below.");
      } catch (e) {
        setNote(e.status === 503
          ? "Place lookup is unavailable right now — enter coordinates below."
          : e.message);
        setManual(true);
      } finally { setLoading(false); }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [q, manual, picked]);

  const pick = (r) => {
    setPicked(r); setQ(r.label); setOpen(false); setResults([]);
    onResolve({ place: r.label, lat: String(r.lat), lon: String(r.lon) });
  };

  const applyManual = (place, lat, lon) => {
    const plat = parseCoord(lat, true), plon = parseCoord(lon, false);
    // invalid coords resolve as empty strings so callers' submit gates stay closed
    onResolve({ place: place.trim(),
      lat: plat === null ? "" : String(plat), lon: plon === null ? "" : String(plon) });
  };
  const badLat = mlat.trim() !== "" && parseCoord(mlat, true) === null;
  const badLon = mlon.trim() !== "" && parseCoord(mlon, false) === null;

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input value={q} placeholder="Start typing a city, e.g. Albany, NY"
          onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          style={{ ...input, width: "100%", boxSizing: "border-box" }} />
        {loading && <span style={{ fontSize: 11, color: C.faint, whiteSpace: "nowrap" }}>…</span>}
      </div>

      {picked && !manual && (
        <div style={{ fontSize: 11, color: C.brass, marginTop: 4 }}>
          ✓ {picked.lat}, {picked.lon}
        </div>
      )}
      {note && <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{note}</div>}

      {open && results.length > 0 && !manual && (
        <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, marginTop: 4,
          background: C.surface, border: `1px solid ${C.line}`, borderRadius: 6,
          overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.45)" }}>
          {results.map((r, i) => (
            <button key={i} onClick={() => pick(r)}
              style={{ display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                background: "transparent", color: C.text, border: "none",
                borderBottom: `1px solid ${C.line}`,
                padding: "8px 12px", fontSize: 12.5, fontFamily: "inherit" }}
              onMouseDown={(e) => e.preventDefault()}>
              {r.label}
              <span style={{ color: C.faint, fontSize: 10.5 }}> · {r.lat}, {r.lon}</span>
            </button>
          ))}
          <div style={{ padding: "4px 12px", fontSize: 9.5, color: C.faint }}>
            Place search © <a href="https://www.openstreetmap.org/copyright" target="_blank"
              rel="noreferrer" style={{ color: C.faint }}>OpenStreetMap contributors</a>
          </div>
        </div>
      )}

      {!manual ? (
        <button onClick={() => setManual(true)}
          style={{ all: "unset", cursor: "pointer", color: C.faint, fontSize: 11, marginTop: 6 }}>
          Can't find it? Enter coordinates manually
        </button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10.5, color: C.faint, marginBottom: 4 }}>
            Manual entry — place name is optional; coordinates are what the chart needs.
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input placeholder="Place (optional)" value={q}
              onChange={(e) => { setQ(e.target.value); applyManual(e.target.value, mlat, mlon); }}
              style={{ ...input, flexGrow: 1, minWidth: 140 }} />
            <input placeholder="Latitude, e.g. 38.58" value={mlat}
              onChange={(e) => { setMlat(e.target.value); applyManual(q, e.target.value, mlon); }}
              style={{ ...input, width: 140,
                borderColor: badLat ? C.rose : undefined }} />
            <input placeholder="Longitude, e.g. -121.49" value={mlon}
              onChange={(e) => { setMlon(e.target.value); applyManual(q, mlat, e.target.value); }}
              style={{ ...input, width: 140,
                borderColor: badLon ? C.rose : undefined }} />
          </div>
          {(badLat || badLon) && (
            <div style={{ fontSize: 10.5, color: C.rose, marginTop: 4 }}>
              Couldn't read {badLat ? "that latitude" : "that longitude"} — plain decimals
              work best (38.58 and -121.49 for Sacramento). N/S/E/W and ° are okay too.
            </div>
          )}
          <button onClick={() => { setManual(false); setNote(""); }}
            style={{ all: "unset", cursor: "pointer", color: C.faint, fontSize: 11, marginTop: 6 }}>
            ← Back to place search
          </button>
        </div>
      )}
    </div>
  );
}
